import { useMemo, useState } from 'react';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import { isSupabaseError } from '../../../../constants/supabaseErrors';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { DisplayedSubjectQuote, SubjectHistoryFeed } from '../../../../types';
import { normalizeQuoteStatus } from '../../../../types';
import { useGetSubTopicQuotes } from '../../api/useGetSubTopicQuotes';
import { useUpdateSubjectQuoteStatus } from '../../api/useUpdateSubjectQuoteStatus';

type Props = {
  subTopicId: string;
};

export function useQuotesTab(props: Props) {
  const { isAdmin } = usePermissions();
  const { data: quotes, isLoading, error } = useGetSubTopicQuotes(props.subTopicId);
  const { mutate: updateQuoteStatus, isPending: isUpdating } = useUpdateSubjectQuoteStatus();

  const [isNewQuoteModalOpen, setIsNewQuoteModalOpen] = useState(false);
  const [quoteToAccept, setQuoteToAccept] = useState<DisplayedSubjectQuote | null>(null);
  const [quoteToEdit, setQuoteToEdit] = useState<DisplayedSubjectQuote | null>(null);
  const [quoteToDelete, setQuoteToDelete] = useState<DisplayedSubjectQuote | null>(null);
  const [quoteToReset, setQuoteToReset] = useState<DisplayedSubjectQuote | null>(null);

  const { totalAcceptedAmount, totalPendingAmount } = useMemo(() => {
    if (!quotes) {
      return {
        totalAcceptedAmount: 0,
        totalPendingAmount: 0,
      };
    }

    let acceptedSum = 0;
    let pendingSum = 0;

    for (const quote of quotes) {
      const status = normalizeQuoteStatus(quote.status);
      if (status === 'accepted' && quote.amount) {
        acceptedSum += Number(quote.amount);
      } else if (status === 'pending' && quote.amount) {
        pendingSum += Number(quote.amount);
      }
    }

    return {
      totalAcceptedAmount: acceptedSum,
      totalPendingAmount: pendingSum,
    };
  }, [quotes]);

  const itemToEdit: SubjectHistoryFeed | undefined = useMemo(() => {
    if (!quoteToEdit) return undefined;
    return {
      id: quoteToEdit.id,
      action_type: 'quote_received',
      date: quoteToEdit.date_received,
      amount: quoteToEdit.amount,
      comment: quoteToEdit.comment,
      title: quoteToEdit.title,
      provider_id: quoteToEdit.provider_id,
      provider_name: quoteToEdit.provider_name ?? quoteToEdit.providers?.name ?? null,
      contact_name: quoteToEdit.contact_name,
      subject_id: quoteToEdit.subject_id,
      space_id: quoteToEdit.space_id,
      created_at: quoteToEdit.created_at,
      created_by: quoteToEdit.created_by,
      all_day: null,
      communication_type: null,
      end_date: null,
      has_documents: (quoteToEdit.documents?.length ?? 0) > 0,
      source: 'subject_actions',
    };
  }, [quoteToEdit]);

  function handleAccept(quote: DisplayedSubjectQuote) {
    setQuoteToAccept(quote);
  }

  function handleRefuse(quote: DisplayedSubjectQuote) {
    updateQuoteStatus(
      {
        quoteId: quote.id,
        subjectId: props.subTopicId,
        status: 'refused',
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Devis marqué comme refusé.', 'info');
        },
        onError: (err: unknown) => {
          const message = isSupabaseError(err) ? err.message : 'Une erreur est survenue lors du refus du devis.';
          enqueueSnackbar(message, 'error');
        },
      }
    );
  }

  function handleResetToPending(quote: DisplayedSubjectQuote) {
    const bpaDoc = quote.documents?.find(
      (doc) =>
        doc.file_url === quote.approved_document_url ||
        doc.document_types?.name?.toLowerCase() === 'bon pour accord' ||
        doc.name?.toLowerCase().includes('accord') ||
        doc.name?.toLowerCase().includes('signé')
    );
    const hasApprovedDocument = Boolean(quote.approved_document_url || bpaDoc);

    if (hasApprovedDocument) {
      setQuoteToReset(quote);
      return;
    }

    updateQuoteStatus(
      {
        quoteId: quote.id,
        subjectId: props.subTopicId,
        status: 'pending',
        clearApprovedDocumentUrl: true,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Devis remis en attente.', 'info');
        },
        onError: (err: unknown) => {
          const message = isSupabaseError(err) ? err.message : 'Une erreur est survenue lors de la réinitialisation.';
          enqueueSnackbar(message, 'error');
        },
      }
    );
  }

  function handleAttachSignedDocument(quote: DisplayedSubjectQuote) {
    setQuoteToAccept(quote);
  }

  function handleEdit(quote: DisplayedSubjectQuote) {
    setQuoteToEdit(quote);
  }

  function handleDelete(quote: DisplayedSubjectQuote) {
    setQuoteToDelete(quote);
  }

  function handleOpenNewQuoteModal() {
    setIsNewQuoteModalOpen(true);
  }

  function handleCloseNewQuoteModal() {
    setIsNewQuoteModalOpen(false);
  }

  function handleCloseAcceptModal() {
    setQuoteToAccept(null);
  }

  function handleCloseEditModal() {
    setQuoteToEdit(null);
  }

  function handleCloseDeleteModal() {
    setQuoteToDelete(null);
  }

  function handleCloseResetModal() {
    setQuoteToReset(null);
  }

  return {
    isAdmin,
    quotes: quotes ?? [],
    totalCount: quotes?.length ?? 0,
    totalAcceptedAmount,
    totalPendingAmount,
    isLoading,
    error,
    isUpdating,
    isNewQuoteModalOpen,
    quoteToAccept,
    quoteToEdit,
    quoteToDelete,
    quoteToReset,
    itemToEdit,
    handleAccept,
    handleRefuse,
    handleResetToPending,
    handleAttachSignedDocument,
    handleEdit,
    handleDelete,
    handleOpenNewQuoteModal,
    handleCloseNewQuoteModal,
    handleCloseAcceptModal,
    handleCloseEditModal,
    handleCloseDeleteModal,
    handleCloseResetModal,
  };
}
